import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import type { SessionType } from '../types';

const STALE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Listens for sessions with status 'extracting' to show a processing indicator.
 * Returns true while any session is still being processed in the background.
 * Automatically marks sessions as failed if they've been extracting for > 5 minutes.
 */
export function useExtractionStatus(
  userId: string | null
): {
  isExtracting: boolean;
  extractingSessionType: SessionType | null;
  extractingSessionId: string | null;
} {
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractingSessionType, setExtractingSessionType] = useState<SessionType | null>(null);
  const [extractingSessionId, setExtractingSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsExtracting(false);
      setExtractingSessionType(null);
      setExtractingSessionId(null);
      return;
    }

    const db = getFirebaseDb();
    const q = query(
      collection(db, 'users', userId, 'sessions'),
      where('status', '==', 'extracting')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        let hasActive = false;
        let latestStartMs = -1;
        let latestType: SessionType | null = null;
        let latestSessionId: string | null = null;

        for (const d of snap.docs) {
          const data = d.data();
          const startedAt = data.startedAt as Timestamp | undefined;
          const startMs = startedAt?.toMillis?.() ?? 0;

          if (startMs > 0 && now - startMs > STALE_THRESHOLD_MS) {
            updateDoc(doc(db, 'users', userId, 'sessions', d.id), {
              status: 'failed',
              extractionError: {
                message: 'Post-session extraction timed out.',
                failedAt: Timestamp.now(),
              },
            }).catch(() => {});
          } else {
            hasActive = true;
            if (startMs >= latestStartMs) {
              latestStartMs = startMs;
              latestType = (data.type as SessionType) ?? null;
              latestSessionId = d.id;
            }
          }
        }

        setIsExtracting(hasActive);
        setExtractingSessionType(hasActive ? latestType : null);
        setExtractingSessionId(hasActive ? latestSessionId : null);
      },
      () => {
        setIsExtracting(false);
        setExtractingSessionType(null);
        setExtractingSessionId(null);
      }
    );

    return unsub;
  }, [userId]);

  return { isExtracting, extractingSessionType, extractingSessionId };
}
